/**
 * ZoneScene: three.js scene with the official shore zones on the water plane.
 *
 * Frames (see contracts.ts): geometry lives in WorldENU (X east, Y up,
 * Z -north, water Y = 0) inside `zones`. In mode 'xr' the zones group matrix
 * is the alignment M = T(tx, waterY, tz) * R_y(yawRad) and three's WebXR
 * manager drives the camera; in 'compass'/'sim' M is identity and the camera
 * comes from CameraPose.
 *
 * Per-frame path (apply + render) allocates nothing.
 */
import * as THREE from 'three';
import type { CameraPose, ENU, LakeENU, RenderMode, WorldAlignment } from '../contracts';
import {
  buildFinishLine,
  buildPierLines,
  buildShoreOccluder,
  buildSlipwayMarkers,
  buildZoneWalls,
  selectTrianglesNear,
  triangulateMultiPolygon,
} from './geometry';
import {
  createFinishMaterial,
  createPierLineMaterial,
  createShoreOccluderMaterial,
  createSlipwayMaterial,
  createSlipwaySignTexture,
  createWallBaseMaterial,
  createWallMaterial,
  createZoneMaterial,
  type SharedUniforms,
} from './materials';
import { RENDER_ORDER, RENDER_STYLE } from './style';

export interface ZoneSceneInput {
  mode: RenderMode;
  /** mode 'xr': XR <- WorldENU alignment. null = no alignment yet (zones hidden). */
  alignment: WorldAlignment | null;
  /** modes 'compass'/'sim': camera pose in WorldENU. */
  camera: CameraPose | null;
  /** Filtered user position (LOD centre, debug helpers). Falls back to the camera position outside XR. */
  userENU: ENU | null;
  /** Camera-forward heading (deg true); places the debug north arrow in XR. */
  headingDeg: number | null;
  /** performance.now() timebase (ms); drives the overlay fade. */
  time: number;
  /** Eye height above the water (m); height of the debug horizon ring. */
  eyeHeightM: number;
  /** Target overlay alpha 0..1 (e.g. 0 on tracking loss, 0.5 when GPS is degraded). Eased with tau 0.2 s. */
  overlayAlpha: number;
  /** settings.overlayOpacity (0.2..1), multiplies all zone opacities. */
  overlayOpacity: number;
  /**
   * settings.viewDistanceM: every overlay layer fades from full opacity at 0.7x this to 0 at
   * this (view-space distance), and is culled beyond it (LOD radius >= this + 500 m).
   */
  viewDistanceM: number;
  /** Show horizon ring and north arrow. */
  debugHelpers: boolean;
  /** Optional aspect override for compass/sim (default: last resize()). */
  aspect?: number;
}

export interface ZoneSceneOptions {
  /** Cap for the device pixel ratio when ZoneScene creates the renderer (default 2). */
  maxPixelRatio?: number;
  /** LOD radius (m) around the user; Infinity draws the whole lake. */
  lodRadius?: number;
  /** Rebuild the drawn subset after this much movement (m). */
  lodRebuildDistance?: number;
}

export interface ZoneSceneStats {
  lakeId: string | null;
  /** Triangles of the full lake per layer. */
  outerTriangles: number;
  innerTriangles: number;
  finishTriangles: number;
  /** Triangles currently submitted (after the LOD radius). */
  drawnTriangles: number;
  /** setLake() build time (ms). */
  buildMs: number;
  lodRebuilds: number;
  /** Current eased global opacity. */
  globalOpacity: number;
}

interface FillLayer {
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  positions: Float32Array;
  fullIndex: Uint32Array;
  index: THREE.BufferAttribute;
  triangles: number;
  drawn: number;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function freeze(o: THREE.Object3D): void {
  o.matrixAutoUpdate = false;
  o.frustumCulled = false;
  o.updateMatrix();
}

export class ZoneScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  /** WorldENU content; its matrix is M in 'xr', identity otherwise. */
  readonly zones = new THREE.Group();

  private readonly ownsRenderer: boolean;
  private readonly shared: SharedUniforms = {
    uGlobal: { value: 0 },
    uFade: { value: new THREE.Vector2(RENDER_STYLE.viewDistanceFadeFrac * 2000, 2000) },
    uUser: { value: new THREE.Vector2(0, 0) },
  };
  private readonly occluderMaterial: THREE.ShaderMaterial;
  private readonly outerMaterial: THREE.ShaderMaterial;
  private readonly innerMaterial: THREE.ShaderMaterial;
  private readonly finishMaterial: THREE.ShaderMaterial;
  private readonly pierMaterial: THREE.ShaderMaterial;
  private readonly slipwayMaterial: THREE.ShaderMaterial;
  private readonly slipwaySignTexture: THREE.Texture;
  private readonly wallInnerMaterial: THREE.ShaderMaterial;
  private readonly wallOuterMaterial: THREE.ShaderMaterial;
  private readonly wallBaseMaterial: THREE.ShaderMaterial;
  private readonly lodRadiusOverride: number | undefined;
  private lodRadius: number;
  private readonly lodRebuildDistance: number;

  private lake: LakeENU | null = null;
  private outer: FillLayer | null = null;
  private inner: FillLayer | null = null;
  private finish: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private pier: THREE.LineSegments<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private slipway: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private osmPierPoints: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private wallInner: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private wallOuter: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private wallInnerBase: THREE.LineSegments<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private wallOuterBase: THREE.LineSegments<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private occluder: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> | null = null;
  private buildMs = 0;
  private lodX = NaN;
  private lodZ = NaN;
  private lodRebuilds = 0;

  private alpha = 0;
  private lastTime = NaN;
  private aspect = 1;
  private lastMode: RenderMode | null = null;

  private readonly helpers = new THREE.Group();
  private readonly horizonRing: THREE.LineLoop<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  private readonly northArrow: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  private readonly simWater: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  private readonly skyColor = new THREE.Color(RENDER_STYLE.simSky);
  private readonly tmpV = new THREE.Vector3();

  constructor(target: THREE.WebGLRenderer | HTMLCanvasElement, options: ZoneSceneOptions = {}) {
    if (target instanceof THREE.WebGLRenderer) {
      this.renderer = target;
      this.ownsRenderer = false;
    } else {
      this.renderer = new THREE.WebGLRenderer({ canvas: target, alpha: true, antialias: true });
      this.renderer.setClearColor(0x000000, 0);
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
      this.renderer.setPixelRatio(Math.min(dpr, options.maxPixelRatio ?? 2));
      this.ownsRenderer = true;
    }
    this.lodRadiusOverride = options.lodRadius;
    this.lodRadius = options.lodRadius ?? RENDER_STYLE.lodRadius;
    this.lodRebuildDistance = options.lodRebuildDistance ?? RENDER_STYLE.lodRebuildDistance;

    this.camera = new THREE.PerspectiveCamera(60, 1, RENDER_STYLE.near, RENDER_STYLE.far);
    this.zones.name = 'zones';
    this.zones.matrixAutoUpdate = false;
    this.scene.add(this.zones);

    this.occluderMaterial = createShoreOccluderMaterial(this.shared);
    this.outerMaterial = createZoneMaterial(RENDER_STYLE.outerOpacity, this.shared);
    this.innerMaterial = createZoneMaterial(RENDER_STYLE.innerOpacity, this.shared);
    this.finishMaterial = createFinishMaterial(this.shared);
    this.pierMaterial = createPierLineMaterial(this.shared);
    this.slipwaySignTexture = createSlipwaySignTexture();
    this.slipwayMaterial = createSlipwayMaterial(this.shared, this.slipwaySignTexture);
    this.wallInnerMaterial = createWallMaterial(RENDER_STYLE.wallInnerOpacity, this.shared);
    this.wallOuterMaterial = createWallMaterial(RENDER_STYLE.wallOuterOpacity, this.shared);
    this.wallBaseMaterial = createWallBaseMaterial(this.shared);

    // Debug helpers (inside `zones`, so they follow the XR alignment).
    this.horizonRing = createHorizonRing();
    this.northArrow = createNorthArrow();
    this.helpers.name = 'debug-helpers';
    this.helpers.add(this.horizonRing, this.northArrow);
    freeze(this.helpers);
    this.helpers.visible = false;
    this.zones.add(this.helpers);

    // Sim-mode background water disc, drawn first.
    const waterGeo = new THREE.CircleGeometry(RENDER_STYLE.simWaterRadius, 180);
    waterGeo.rotateX(-Math.PI / 2);
    this.simWater = new THREE.Mesh(
      waterGeo,
      new THREE.MeshBasicMaterial({ color: RENDER_STYLE.simWater, depthTest: false, depthWrite: false }),
    );
    this.simWater.name = 'sim-water';
    this.simWater.renderOrder = RENDER_ORDER.simWater;
    freeze(this.simWater);
    this.simWater.visible = false;
    this.scene.add(this.simWater);
  }

  /** Builds the zone meshes for a lake (once per lake). null removes them. */
  setLake(lake: LakeENU | null): void {
    if (lake === this.lake) return;
    this.clearLake();
    this.lake = lake;
    if (!lake) return;

    const t0 = performance.now();

    const occ = buildShoreOccluder(lake.lakeOutline, RENDER_STYLE.shoreOccluderMaxSegment);
    if (occ.triangleCount > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(occ.positions, 3));
      g.setAttribute('aSide', new THREE.BufferAttribute(occ.side, 1));
      g.setIndex(new THREE.BufferAttribute(occ.indices, 1));
      const m = new THREE.Mesh(g, this.occluderMaterial);
      m.name = 'shore-occluder';
      m.renderOrder = RENDER_ORDER.occluder;
      freeze(m);
      this.zones.add(m);
      this.occluder = m;
    }

    this.outer = this.createFill(lake.outer, this.outerMaterial, RENDER_ORDER.outer, 'outer');
    this.inner = this.createFill(lake.inner, this.innerMaterial, RENDER_ORDER.inner, 'inner');

    const fl = buildFinishLine(lake.extendedLines, RENDER_STYLE.finishMaxSegment);
    if (fl.triangleCount > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(fl.positions, 3));
      g.setAttribute('aSide', new THREE.BufferAttribute(fl.side, 1));
      g.setAttribute('aAlong', new THREE.BufferAttribute(fl.along, 1));
      g.setIndex(new THREE.BufferAttribute(fl.indices, 1));
      const m = new THREE.Mesh(g, this.finishMaterial);
      m.name = 'finish-line';
      m.renderOrder = RENDER_ORDER.finish;
      freeze(m);
      this.zones.add(m);
      this.finish = m;
    }

    const pl = buildPierLines([...lake.pierLines, ...lake.osmPierLines]);
    if (pl.segmentCount > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pl.positions, 3));
      const m = new THREE.LineSegments(g, this.pierMaterial);
      m.name = 'pier-lines';
      m.renderOrder = RENDER_ORDER.pier;
      freeze(m);
      this.zones.add(m);
      this.pier = m;
    }

    const walls = buildZoneWalls(lake.outer, lake.inner, lake.lakeOutline, RENDER_STYLE.wallHeight);
    if (walls.innerAdjacent.triangleCount > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(walls.innerAdjacent.positions, 3));
      g.setIndex(new THREE.BufferAttribute(walls.innerAdjacent.indices, 1));
      const m = new THREE.Mesh(g, this.wallInnerMaterial);
      m.name = 'wall-inner';
      m.renderOrder = RENDER_ORDER.wall;
      freeze(m);
      this.zones.add(m);
      this.wallInner = m;

      const bg = new THREE.BufferGeometry();
      bg.setAttribute('position', new THREE.BufferAttribute(walls.innerAdjacent.basePositions, 3));
      const bm = new THREE.LineSegments(bg, this.wallBaseMaterial);
      bm.name = 'wall-inner-base';
      bm.renderOrder = RENDER_ORDER.wallBase;
      freeze(bm);
      this.zones.add(bm);
      this.wallInnerBase = bm;
    }
    if (walls.outerLimit.triangleCount > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(walls.outerLimit.positions, 3));
      g.setIndex(new THREE.BufferAttribute(walls.outerLimit.indices, 1));
      const m = new THREE.Mesh(g, this.wallOuterMaterial);
      m.name = 'wall-outer';
      m.renderOrder = RENDER_ORDER.wall;
      freeze(m);
      this.zones.add(m);
      this.wallOuter = m;

      const bg = new THREE.BufferGeometry();
      bg.setAttribute('position', new THREE.BufferAttribute(walls.outerLimit.basePositions, 3));
      const bm = new THREE.LineSegments(bg, this.wallBaseMaterial);
      bm.name = 'wall-outer-base';
      bm.renderOrder = RENDER_ORDER.wallBase;
      freeze(bm);
      this.zones.add(bm);
      this.wallOuterBase = bm;
    }

    const sw = buildSlipwayMarkers(lake.slipways, { icon: true });
    if (sw.triangleCount > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(sw.positions, 3));
      g.setAttribute('aOffset', new THREE.BufferAttribute(sw.offset, 2));
      g.setAttribute('aUv', new THREE.BufferAttribute(sw.uv, 2));
      g.setAttribute('aColor', new THREE.BufferAttribute(sw.color, 3));
      g.setIndex(new THREE.BufferAttribute(sw.indices, 1));
      const m = new THREE.Mesh(g, this.slipwayMaterial);
      m.name = 'slipway-markers';
      m.renderOrder = RENDER_ORDER.slipway;
      freeze(m);
      this.zones.add(m);
      this.slipway = m;
    }

    const opp = buildSlipwayMarkers(lake.osmPierPoints, { fillColor: RENDER_STYLE.osmPierPointColor, icon: false });
    if (opp.triangleCount > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(opp.positions, 3));
      g.setAttribute('aOffset', new THREE.BufferAttribute(opp.offset, 2));
      g.setAttribute('aUv', new THREE.BufferAttribute(opp.uv, 2));
      g.setAttribute('aColor', new THREE.BufferAttribute(opp.color, 3));
      g.setIndex(new THREE.BufferAttribute(opp.indices, 1));
      const m = new THREE.Mesh(g, this.slipwayMaterial);
      m.name = 'osm-pier-points';
      m.renderOrder = RENDER_ORDER.slipway;
      freeze(m);
      this.zones.add(m);
      this.osmPierPoints = m;
    }

    this.buildMs = performance.now() - t0;
    this.lodX = NaN;
    this.lodZ = NaN;
  }

  /** Per-frame update. No allocations. */
  apply(input: ZoneSceneInput): void {
    // Overlay fade.
    const target = clamp01(input.overlayAlpha);
    const dt = (input.time - this.lastTime) / 1000;
    if (!(dt >= 0) || dt > 1) this.alpha = target;
    else this.alpha += (target - this.alpha) * (1 - Math.exp(-dt / RENDER_STYLE.overlayFadeTau));
    this.lastTime = input.time;
    const global = this.alpha * clamp01(input.overlayOpacity);
    this.shared.uGlobal.value = global;

    const viewDistance = input.viewDistanceM;
    this.shared.uFade.value.set(RENDER_STYLE.viewDistanceFadeFrac * viewDistance, viewDistance);
    if (this.lodRadiusOverride === undefined) {
      const radius = Math.max(RENDER_STYLE.lodRadius, viewDistance + RENDER_STYLE.lodRadiusMargin);
      if (radius !== this.lodRadius) {
        this.lodRadius = radius;
        this.lodX = NaN; // force an immediate LOD rebuild at the new radius
        this.lodZ = NaN;
      }
    }

    const mode = input.mode;
    const modeChanged = mode !== this.lastMode;
    this.lastMode = mode;
    const cam = this.camera;
    let aligned = true;

    if (mode === 'xr') {
      const a = input.alignment;
      if (a) {
        const c = Math.cos(a.yawRad);
        const s = Math.sin(a.yawRad);
        // T(tx, waterY, tz) * R_y(yaw), row-major arguments.
        this.zones.matrix.set(c, 0, s, a.tx, 0, 1, 0, a.waterY, -s, 0, c, a.tz, 0, 0, 0, 1);
      } else {
        aligned = false;
      }
      this.zones.matrixWorldNeedsUpdate = true;
      this.scene.background = null;
      this.simWater.visible = false;
      if (modeChanged) {
        cam.near = RENDER_STYLE.near;
        cam.far = RENDER_STYLE.far;
      }
    } else {
      if (modeChanged) {
        this.zones.matrix.identity();
        this.zones.matrixWorldNeedsUpdate = true;
      }
      const p = input.camera;
      if (p) {
        cam.position.set(p.position[0], p.position[1], p.position[2]);
        cam.quaternion.set(p.quaternion[0], p.quaternion[1], p.quaternion[2], p.quaternion[3]);
        const aspect = input.aspect ?? this.aspect;
        if (modeChanged || cam.fov !== p.vFovDeg || cam.aspect !== aspect) {
          cam.fov = p.vFovDeg;
          cam.aspect = aspect;
          cam.near = RENDER_STYLE.near;
          cam.far = RENDER_STYLE.far;
          cam.updateProjectionMatrix();
        }
      }
      const sim = mode === 'sim';
      this.scene.background = sim ? this.skyColor : null;
      this.simWater.visible = sim;
      if (sim) {
        this.simWater.matrix.makeTranslation(cam.position.x, 0, cam.position.z);
        this.simWater.matrixWorldNeedsUpdate = true;
      }
    }

    // User position in WorldENU.
    let ux = 0;
    let uz = 0;
    let haveUser = false;
    if (input.userENU) {
      ux = input.userENU.e;
      uz = -input.userENU.n;
      haveUser = true;
    } else if (mode !== 'xr' && input.camera) {
      ux = input.camera.position[0];
      uz = input.camera.position[2];
      haveUser = true;
    }
    this.shared.uUser.value.set(ux, uz);

    const showZones = aligned && global > 0.002;
    if (this.occluder) this.occluder.visible = showZones;
    if (this.outer) this.outer.mesh.visible = showZones;
    if (this.inner) this.inner.mesh.visible = showZones;
    if (this.finish) this.finish.visible = showZones;
    if (this.pier) this.pier.visible = showZones;
    if (this.wallInner) this.wallInner.visible = showZones;
    if (this.wallOuter) this.wallOuter.visible = showZones;
    if (this.wallInnerBase) this.wallInnerBase.visible = showZones;
    if (this.wallOuterBase) this.wallOuterBase.visible = showZones;
    if (this.slipway) this.slipway.visible = showZones;
    if (this.osmPierPoints) this.osmPierPoints.visible = showZones;
    if (haveUser && this.lake) this.updateLod(ux, uz);

    // Debug helpers.
    const helpersOn = input.debugHelpers && haveUser && aligned;
    this.helpers.visible = helpersOn;
    if (helpersOn) this.placeHelpers(input, ux, uz);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /** CSS pixel size of the view. Resizes the renderer only if ZoneScene created it. */
  resize(width: number, height: number): void {
    if (width <= 0 || height <= 0) return;
    this.aspect = width / height;
    if (this.ownsRenderer && !this.renderer.xr.isPresenting) this.renderer.setSize(width, height, false);
    if (!this.renderer.xr.isPresenting) {
      this.camera.aspect = this.aspect;
      this.camera.updateProjectionMatrix();
    }
  }

  getStats(): ZoneSceneStats {
    return {
      lakeId: this.lake?.meta.id ?? null,
      outerTriangles: this.outer?.triangles ?? 0,
      innerTriangles: this.inner?.triangles ?? 0,
      finishTriangles: this.finish ? this.finish.geometry.index!.count / 3 : 0,
      drawnTriangles:
        (this.outer?.drawn ?? 0) + (this.inner?.drawn ?? 0) + (this.finish ? this.finish.geometry.index!.count / 3 : 0),
      buildMs: this.buildMs,
      lodRebuilds: this.lodRebuilds,
      globalOpacity: this.shared.uGlobal.value,
    };
  }

  dispose(): void {
    this.clearLake();
    this.occluderMaterial.dispose();
    this.outerMaterial.dispose();
    this.innerMaterial.dispose();
    this.finishMaterial.dispose();
    this.pierMaterial.dispose();
    this.slipwayMaterial.dispose();
    this.slipwaySignTexture.dispose();
    this.wallInnerMaterial.dispose();
    this.wallOuterMaterial.dispose();
    this.wallBaseMaterial.dispose();
    for (const o of [this.horizonRing, this.northArrow, this.simWater]) {
      o.geometry.dispose();
      o.material.dispose();
    }
    this.scene.clear();
    if (this.ownsRenderer) this.renderer.dispose();
  }

  // ─── internals ────────────────────────────────────────────────────────────

  private createFill(
    mp: LakeENU['inner'],
    material: THREE.ShaderMaterial,
    order: number,
    name: string,
  ): FillLayer | null {
    const t = triangulateMultiPolygon(mp);
    if (t.triangleCount === 0) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(t.positions, 3));
    // Drawn subset, rewritten on LOD rebuilds; starts as the full lake.
    const index = new THREE.BufferAttribute(new Uint32Array(t.indices), 1);
    index.setUsage(THREE.DynamicDrawUsage);
    g.setIndex(index);
    const mesh = new THREE.Mesh(g, material);
    mesh.name = name;
    mesh.renderOrder = order;
    freeze(mesh);
    this.zones.add(mesh);
    return { mesh, positions: t.positions, fullIndex: t.indices, index, triangles: t.triangleCount, drawn: t.triangleCount };
  }

  private updateLod(x: number, z: number): void {
    if (!Number.isFinite(this.lodRadius)) return;
    const dx = x - this.lodX;
    const dz = z - this.lodZ;
    if (dx * dx + dz * dz < this.lodRebuildDistance * this.lodRebuildDistance) return; // NaN -> false -> rebuild
    this.lodX = x;
    this.lodZ = z;
    this.lodRebuilds++;
    if (this.outer) this.rebuildLayer(this.outer, x, z);
    if (this.inner) this.rebuildLayer(this.inner, x, z);
  }

  private rebuildLayer(layer: FillLayer, x: number, z: number): void {
    const n = selectTrianglesNear(layer.positions, layer.fullIndex, layer.index.array as Uint32Array, x, z, this.lodRadius);
    layer.drawn = n / 3;
    layer.mesh.geometry.setDrawRange(0, n);
    layer.index.clearUpdateRanges();
    layer.index.addUpdateRange(0, n);
    layer.index.needsUpdate = true;
  }

  private placeHelpers(input: ZoneSceneInput, ux: number, uz: number): void {
    this.horizonRing.matrix.makeTranslation(ux, input.eyeHeightM, uz);

    // Horizontal forward direction: camera quaternion outside XR, heading in XR.
    let fx = 0;
    let fz = -1;
    const f = this.tmpV;
    if (input.mode !== 'xr' && input.camera) {
      // apply() already copied the pose into this.camera.
      f.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
      const len = Math.hypot(f.x, f.z);
      if (len > 1e-3) {
        fx = f.x / len;
        fz = f.z / len;
      }
    } else if (input.headingDeg !== null) {
      const h = THREE.MathUtils.degToRad(input.headingDeg);
      fx = Math.sin(h);
      fz = -Math.cos(h);
    }
    const d = RENDER_STYLE.northArrowDistance;
    this.northArrow.matrix.makeTranslation(ux + fx * d, 0.02, uz + fz * d);
    this.helpers.matrixWorldNeedsUpdate = true;
    this.horizonRing.matrixWorldNeedsUpdate = true;
    this.northArrow.matrixWorldNeedsUpdate = true;
  }

  private clearLake(): void {
    if (this.occluder) {
      this.zones.remove(this.occluder);
      this.occluder.geometry.dispose();
      this.occluder = null;
    }
    for (const layer of [this.outer, this.inner]) {
      if (!layer) continue;
      this.zones.remove(layer.mesh);
      layer.mesh.geometry.dispose();
    }
    if (this.finish) {
      this.zones.remove(this.finish);
      this.finish.geometry.dispose();
    }
    if (this.pier) {
      this.zones.remove(this.pier);
      this.pier.geometry.dispose();
    }
    if (this.slipway) {
      this.zones.remove(this.slipway);
      this.slipway.geometry.dispose();
    }
    if (this.osmPierPoints) {
      this.zones.remove(this.osmPierPoints);
      this.osmPierPoints.geometry.dispose();
    }
    if (this.wallInner) {
      this.zones.remove(this.wallInner);
      this.wallInner.geometry.dispose();
    }
    if (this.wallOuter) {
      this.zones.remove(this.wallOuter);
      this.wallOuter.geometry.dispose();
    }
    if (this.wallInnerBase) {
      this.zones.remove(this.wallInnerBase);
      this.wallInnerBase.geometry.dispose();
    }
    if (this.wallOuterBase) {
      this.zones.remove(this.wallOuterBase);
      this.wallOuterBase.geometry.dispose();
    }
    this.outer = this.inner = null;
    this.finish = null;
    this.pier = null;
    this.slipway = null;
    this.osmPierPoints = null;
    this.wallInner = null;
    this.wallOuter = null;
    this.wallInnerBase = null;
    this.wallOuterBase = null;
    this.lake = null;
  }
}

function createHorizonRing(): THREE.LineLoop<THREE.BufferGeometry, THREE.LineBasicMaterial> {
  const n = 720;
  const r = RENDER_STYLE.horizonRadius;
  const pos = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    pos[i * 3] = Math.cos(a) * r;
    pos[i * 3 + 2] = Math.sin(a) * r;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const ring = new THREE.LineLoop(
    g,
    new THREE.LineBasicMaterial({
      color: RENDER_STYLE.horizonColor,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      depthWrite: false,
    }),
  );
  ring.name = 'horizon-ring';
  ring.renderOrder = RENDER_ORDER.helpers;
  freeze(ring);
  return ring;
}

/** Flat arrow on the water pointing north (-Z). */
function createNorthArrow(): THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> {
  const L = RENDER_STYLE.northArrowLength;
  const W = RENDER_STYLE.northArrowWidth;
  const head = L * 0.45;
  const shaft = W * 0.18;
  const s = new THREE.Shape();
  // Shape in XY with the tip at +Y; rotateX(-90deg) maps +Y to -Z (north).
  s.moveTo(0, L / 2);
  s.lineTo(W / 2, L / 2 - head);
  s.lineTo(shaft, L / 2 - head);
  s.lineTo(shaft, -L / 2);
  s.lineTo(-shaft, -L / 2);
  s.lineTo(-shaft, L / 2 - head);
  s.lineTo(-W / 2, L / 2 - head);
  s.closePath();
  const g = new THREE.ShapeGeometry(s);
  g.rotateX(-Math.PI / 2);
  const arrow = new THREE.Mesh(
    g,
    new THREE.MeshBasicMaterial({
      color: RENDER_STYLE.northArrowColor,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    }),
  );
  arrow.name = 'north-arrow';
  arrow.renderOrder = RENDER_ORDER.helpers;
  freeze(arrow);
  return arrow;
}
